import React, { useMemo, useEffect } from 'react'
import { type UseFormReturn } from 'react-hook-form-7'
import { Select } from '@retailer-platform/shared-components/src/common/select/Select'
import {
  type SelectProps,
  type SimpleOption,
} from '@retailer-platform/shared-components/src/common/select/utils/select.types'
import { useAccountManagementPartners } from 'domains/account-management/components/partner-select/utils/AccountManagementPartnerSelect.hooks'
import { type FormData } from 'domains/account-management/components/account-form/utils/AccountForm.types'
import { useDomainMessages } from 'domains/account-management/utils/domain/intl'
import { idsSelectStyles } from 'domains/account-management/styles/accountManagement.styles'

export interface Props<TValue>
  extends Omit<SelectProps<SimpleOption<TValue>>, 'options' | 'isMulti'> {
  form: UseFormReturn<FormData>
  isUpdate: boolean
  allowMultiSelect?: boolean
  partnerIdContext?: string
  isDisabled?: boolean
}

interface OptionType {
  label: string
  description: string
  value: string
  slug: string
}

export const AccountManagementPartnerSelect: React.FunctionComponent<Props<OptionType>> = ({
  onChange,
  allowMultiSelect,
  partnerIdContext,
  form,
  isDisabled,
  isUpdate,
  ...props
}) => {
  // watching here because react-hook-form doesn't always send an updated value
  const value = form.watch('partners')

  const useMultiSelect: boolean = allowMultiSelect || value?.length > 1
  const maxValuesReached = value?.length >= 50

  const { options, loading, error, onSelectChange } = useAccountManagementPartners({
    onChange,
    useMultiSelect,
  })

  const i18n = useDomainMessages({
    placeholderMessage: 'accountManagementDomain.internalTerms.partner.placeholder',
    loadingMessage: 'accountManagementDomain.internalTerms.partners.loading',
    maxOptions: 'accountManagementDomain.internalTerms.partners.maxOptionsReached',
    noOptions: 'accountManagementDomain.internalTerms.partners.noOptions',
  })

  const constrainedOptions = useMemo(() => {
    // If we reach the maximum values, we hide the options and display a custom message.
    if (maxValuesReached) return []

    // If we are viewing form in partner ID context, we restrict partner selection to that partner only
    if (partnerIdContext) return options.filter(opt => partnerIdContext.toString() === opt.value)

    // Otherwise, use all options.
    return options
  }, [options, maxValuesReached, partnerIdContext])

  const constrainedNoOptionsMessage = maxValuesReached ? i18n.maxOptions : i18n.noOptions

  useEffect(() => {
    if (!isUpdate && !loading && !isDisabled && constrainedOptions.length === 1) {
      onSelectChange(constrainedOptions[0])
    }
  }, [constrainedOptions, isDisabled, isUpdate, loading, onSelectChange, useMultiSelect])

  return (
    <Select
      loading={loading}
      loadingMessage={i18n.loadingMessage}
      options={constrainedOptions}
      error={error}
      placeholder={i18n.placeholderMessage}
      isDisabled={isDisabled}
      {...props}
      onChange={onSelectChange}
      isMulti={useMultiSelect}
      emptyValue={undefined}
      value={value}
      noOptionsMessage={() => constrainedNoOptionsMessage}
      styles={{
        control: idsSelectStyles.control,
      }}
    />
  )
}
